import { useCallback, useEffect, useRef } from "react";
import Slider, { SliderProps } from "@mui/material/Slider";

import useGetVideoInfo from "../../../hooks/useGetVideoInfo";
import formatTime from "../../../utils/formatTime";
import { useAppContext } from "../../../hooks/useAppContext";

// 1. Import the Autonomous Brain
import { useKernel } from "../../../hooks/AIKernel"; // Adjust relative path as needed

const START = 0;
const END = 1;

const VideoEditorTimeline: React.FC = () => {
  const {
    setTimelineStartTime,
    timelineStartTime,
    setTimelineEndTime,
    timelineEndTime,
    setCurrentSeekTime,
    isProcessing,
    currentSeekTime,
    videoRef,
  } = useAppContext();

  const { currentTime, duration } = useGetVideoInfo();

  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();

  // Ref to track rapid trimming adjustments
  const trimAdjustments = useRef<number[]>([]);

  const handleTimelineChange = useCallback<
    NonNullable<SliderProps["onChange"]>
  >(
    (e, values, activeThumb) => {
      e.preventDefault();

      const [startTime, endTime] = values as number[];

      if (e.type === "mousedown" || e.type === "touchstart") {
        return;
      }

      switch (activeThumb) {
        case START:
          setTimelineStartTime(startTime);
          break;
        case END:
          setTimelineEndTime(endTime);
          break;
      }
    },
    [setTimelineEndTime, setTimelineStartTime]
  );

  const handleChangecommit = useCallback<
    NonNullable<SliderProps["onChangeCommitted"]>
  >(
    (e, values) => {
      e.preventDefault();

      const [startTime, endTime] = values as number[];

      if (!videoRef) return;

      if (currentSeekTime < startTime) {
        setCurrentSeekTime(startTime);
        videoRef.currentTime = startTime;
      }

      if (currentSeekTime > endTime) {
        setCurrentSeekTime(endTime);
        videoRef.currentTime = endTime;
      }

      // 3. TELEMETRY: Track trimming precision & intent
      if (reportBehavior) {
        const now = Date.now();
        trimAdjustments.current.push(now);
        
        // Filter adjustments made in the last 5 seconds
        trimAdjustments.current = trimAdjustments.current.filter(t => now - t < 5000);

        if (trimAdjustments.current.length > 3) {
          // TELEMETRY: User is tweaking the timeline repeatedly (high precision/struggling)
          reportBehavior("high_precision_trimming", 3, "behavior");
          trimAdjustments.current = []; // reset after recording
        } else {
          // TELEMETRY: Standard editing interest
          reportBehavior("manual_video_trim", 1, "interest");
        }
      }
    },
    [currentSeekTime, setCurrentSeekTime, videoRef, reportBehavior]
  );

  useEffect(() => {
    setCurrentSeekTime(currentTime);
  }, [currentTime, setCurrentSeekTime]);

  // 4. ADAPTIVE UI VARIABLES
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const highlightCTA = agentState?.ui?.highlightCTAs;
  
  // Dynamic Colors based on Neural Theme
  const thumbColor = isDarkMode ? "#00e5ff" : "#ff7800"; // Cyan for dark, Orange for light
  const trackColor = isDarkMode ? "rgba(0, 229, 255, 0.5)" : "rgba(255, 120, 0, 0.5)";
  const railColor = isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)";
  const shadowColor = isDarkMode ? "rgba(0, 229, 255, 0.5)" : "#bebebe";

  return (
    <Slider
      disabled={isProcessing}
      min={0}
      max={duration}
      step={1}
      value={[timelineStartTime, timelineEndTime]}
      valueLabelDisplay="auto"
      valueLabelFormat={formatTime}
      onChange={handleTimelineChange}
      onChangeCommitted={handleChangecommit}
      sx={{
        height: 80,
        padding: "0 !important",
        borderRadius: 0,
        color: trackColor, // Sets the track color between the thumbs
        
        "& .MuiSlider-rail": {
          backgroundColor: railColor,
          opacity: 1,
        },

        "& .MuiSlider-thumb": {
          zIndex: 3,
          cursor: "col-resize",
          border: "none",
          top: 0,
          transform: "translateX(-50%)",
          height: 80,
          width: 10,
          backgroundColor: thumbColor,
          borderRadius: 5,
          opacity: 0.95,
          boxShadow: `0 0 4px 1px ${shadowColor}`,
          transition: "box-shadow 0.3s ease, background-color 0.5s ease",
          
          // ADAPTIVE UI: Pulse if user is experiencing cognitive friction
          animation: highlightCTA && !isProcessing ? "pulse-thumb 2s infinite" : "none",
          
          "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
            boxShadow: `0 0 8px 2px ${shadowColor}`,
          },
          "&::before, &::after": {
            display: "none",
          },
        },

        "& .MuiSlider-track": {
          borderRadius: "5px",
          backgroundColor: trackColor,
          transition: "background-color 0.5s ease",
        },

        "@keyframes pulse-thumb": {
          "0%": { boxShadow: `0 0 0 0 ${shadowColor}` },
          "70%": { boxShadow: `0 0 0 8px rgba(0,0,0,0)` },
          "100%": { boxShadow: `0 0 0 0 rgba(0,0,0,0)` }
        }
      }}
    />
  );
};

export default VideoEditorTimeline;